var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { defineChart } from "@tanstack/charts";
import { geoShape } from "@tanstack/charts/geo";
import { decorative } from "@tanstack/charts/mark/decorative";
import { tooltip } from "@tanstack/charts/tooltip";
import { geoMercator } from "d3-geo";
import { consume } from "@lit/context";
import { html, LitElement, nothing, unsafeCSS } from "lit";
import { customElement } from "lit/decorators.js";
import { stylePref } from "../../../../constants/prefs.ts";
import { dateOnlyFormat } from "../../../../utils/index.ts";
import { cache } from "../../../../utils/lit.ts";
import base from "../../../../styles/utility/baseline.css?type=raw";
import styles from "./city.css?type=raw";
import { groupCityEvents } from "./city-data.ts";
import { renderSvg } from "../svg.ts";
import gigs from "../gigs.json" with { type: "json" };
import england from "./england.json" with { type: "json" };
import "../../register.ts";
const cities = groupCityEvents(gigs.events);
const outline = {
    type: "MultiPolygon",
    coordinates: england.coordinates,
};
const frame = {
    type: "MultiPoint",
    coordinates: cities.map((row) => row.geometry.coordinates),
};
const createMap = () => {
    const projection = ({ chart: { x, y, width, height } }) => geoMercator()
        .fitExtent([
        [x + 64, y + 40],
        [x + width - 64, y + height - 40],
    ], frame)
        .clipExtent([
        [x, y],
        [x + width, y + height],
    ]);
    const definition = defineChart({
        margin: 0,
        scales: { x: null, y: null },
        marks: [
            decorative(geoShape([outline], {
                id: "england-outline",
                projection,
                fill: "var(--gigs-land-color, light-dark(var(--green0, #dafbe1), var(--green-30, #25452d)))",
                stroke: "var(--divider)",
                strokeWidth: 1,
            })),
            geoShape(cities, {
                id: "gigs-by-city",
                projection,
                key: (row) => row.properties.city,
                r: (row) => Math.sqrt(row.properties.count) * 3,
                fill: "var(--gigs-city-color, var(--button, #0550ae))",
                fillOpacity: 0.7,
                stroke: "var(--page-foreground)",
                strokeWidth: 1,
            }),
        ],
        tooltip,
    });
    const renderMap = (scene, options) => {
        const project = projection({ chart: scene.chart, data: [] });
        return renderSvg({
            ...scene,
            nodes: [
                ...scene.nodes,
                ...cities.map((row) => {
                    const point = project(row.geometry.coordinates);
                    if (!point)
                        throw new Error(`Could not project ${row.properties.city}`);
                    const below = row.properties.city === "Trowbridge" ||
                        row.properties.city === "Brighton" ||
                        row.properties.city === "Bath";
                    const left = row.properties.city === "Bristol" || row.properties.city === "Bath";
                    const right = row.properties.city === "Bradford-on-Avon" || row.properties.city === "Trowbridge";
                    return {
                        kind: "label",
                        key: `city-label:${row.properties.city}`,
                        x: point[0] + (left ? -16 : right ? 10 : 0),
                        y: point[1] + (below ? 20 : -20),
                        text: row.properties.city,
                        anchor: left ? "end" : right ? "start" : "middle",
                        style: { fill: "var(--page-foreground)" },
                    };
                }),
            ],
        }, options);
    };
    return { definition, renderSvg: renderMap };
};
const mainMap = createMap();
const renderEvents = (pageStyle) => ({ points }) => {
    const city = points[0]?.datum.properties;
    if (!city)
        return nothing;
    return html `
      <div class="city-tooltip caption">
        <strong>${city.city} · ${city.count} event${city.count !== 1 ? "s" : ""}</strong>
        <ul>
          ${city.events.map((event) => html `
              <li>
                <strong>${event.artists.join(", ")}</strong>
                ${event.support?.length
        ? html `<div class="city-tooltip-support">
                      Support: ${event.support.join(", ")}
                    </div>`
        : null}
                <div>
                  <time datetime=${event.date}>${dateOnlyFormat(event.date, pageStyle)}</time>
                  · ${event.venue}
                </div>
              </li>
            `)}
        </ul>
      </div>
    `;
};
let GigsByCity = class GigsByCity extends LitElement {
    constructor() {
        super(...arguments);
        this.pageStyle = stylePref.fallback;
    }
    static { this.styles = [unsafeCSS(base), unsafeCSS(styles)]; }
    get chartOptions() {
        const renderTooltipBody = renderEvents(this.pageStyle);
        return {
            ...mainMap,
            height: 640,
            ariaLabel: "Gigs by city on an England map",
            renderTooltipBody,
        };
    }
    render() {
        return html `
      <h2 class="headline6">Gigs by city</h2>
      <p class="caption">Circle area represents event count</p>
      <tanstack-chart .options=${this.chartOptions}></tanstack-chart>
      <ul class="sr-only" aria-label="Gigs by city">
        ${cities.map(({ properties: city }) => html `<li>${city.city}: ${city.count}</li>`)}
      </ul>
    `;
    }
};
__decorate([
    consume({ context: stylePref.context, subscribe: true })
], GigsByCity.prototype, "pageStyle", void 0);
__decorate([
    cache(({ pageStyle }) => [pageStyle])
], GigsByCity.prototype, "chartOptions", null);
GigsByCity = __decorate([
    customElement("gigs-by-city")
], GigsByCity);
export { GigsByCity };
